import { useState, useEffect, useMemo } from "react";
import { API_URL } from "../config";

function GenerateImages({ userEmail }) {
  const [prompt, setPrompt] = useState("");
  const [width, setWidth] = useState(640);
  const [height, setHeight] = useState(1136);
  const [batch, setBatch] = useState(2);
  const [idLabel, setIdLabel] = useState(null);
  const [labels, setLabels] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [workflowId, setWorkflowId] = useState("");
  const [availableLoras, setAvailableLoras] = useState([]);
  const [selectedLoras, setSelectedLoras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(null);
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/approve/labels`)
      .then((res) => res.json())
      .then((data) => setLabels(data))
      .catch((err) => console.error("Failed to load labels:", err));

    fetch(`${API_URL}/api/workflows`)
      .then((res) => res.json())
      .then((data) => {
        setWorkflows(data);
        if (data.length > 0) {
          const def = data[0];
          setWorkflowId(def.id);
          setWidth(def.default_width);
          setHeight(def.default_height);
          setBatch(def.default_batch);
        }
      })
      .catch((err) => console.error("Failed to load workflows:", err));

    fetch(`${API_URL}/api/loras`)
      .then((res) => res.json())
      .then((data) => setAvailableLoras(data.loras || []))
      .catch((err) => console.error("Failed to load LoRAs:", err));
  }, []);

  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API_URL}/api/generations?user_email=${encodeURIComponent(userEmail)}`)
      .then((res) => res.json())
      .then((data) => setImages(data))
      .catch((err) => console.error("Failed to load history:", err));
  }, [userEmail]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const img of images) {
      if (!map.has(img.job_id)) {
        map.set(img.job_id, {
          jobId: img.job_id,
          prompt: img.prompt,
          timestamp: img.created_at,
          labelName: labels.find((l) => l.id === img.id_label)?.name || "\u2014",
          images: [],
        });
      }
      map.get(img.job_id).images.push(img);
    }
    return Array.from(map.values());
  }, [images, labels]);

  function toggleImage(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleGroup(group) {
    const groupIds = group.images.map((img) => img.id);
    const allSelected = groupIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      groupIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  async function handleAction(action) {
    if (selected.size === 0) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/generations/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || errData.detail || "Action failed");
      }
      setImages((prev) => prev.filter((img) => !selected.has(img.id)));
      setSelected(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  const currentWorkflow = workflows.find((w) => w.id === workflowId);

  function handleWorkflowChange(id) {
    setWorkflowId(id);
    const wf = workflows.find((w) => w.id === id);
    if (wf) {
      setWidth(wf.default_width);
      setHeight(wf.default_height);
      setBatch(wf.default_batch);
      if (!wf.supports_lora) setSelectedLoras([]);
    }
  }

  function addLora(loraName) {
    if (!loraName || selectedLoras.some((l) => l.lora_name === loraName)) return;
    setSelectedLoras((prev) => [...prev, { lora_name: loraName, strength_model: 1.0, strength_clip: 1.0 }]);
  }

  function removeLora(index) {
    setSelectedLoras((prev) => prev.filter((_, i) => i !== index));
  }

  function updateLoraStrength(index, field, value) {
    setSelectedLoras((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }

  async function handleGenerate(e) {
    e.preventDefault();
    if (!prompt.trim() || !idLabel) return;

    setLoading(true);
    setError(null);
    setProgress(null);
    setStatus(null);

    try {
      const res = await fetch(`${API_URL}/api/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt, width, height, batch,
          user_email: userEmail,
          id_label: idLabel,
          workflow_id: workflowId,
          loras: selectedLoras.length > 0 ? selectedLoras : undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Generation failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));

          if (data.type === "status") setStatus(data.message);
          else if (data.type === "progress") setProgress({ value: data.value, max: data.max });
          else if (data.type === "complete") {
            const freshRes = await fetch(`${API_URL}/api/generations?user_email=${encodeURIComponent(userEmail)}`);
            const freshData = await freshRes.json();
            setImages(freshData);
          } else if (data.type === "error") throw new Error(data.message);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setProgress(null);
      setStatus(null);
    }
  }

  const pct = progress ? Math.round((progress.value / progress.max) * 100) : 0;

  return (
    <div>
      <h3 className="mb-4">Generate Images</h3>

      <form onSubmit={handleGenerate}>
        <div className="mb-3">
          <label className="form-label fw-semibold">Book (Label)</label>
          <select className="form-select" value={idLabel || ""} onChange={(e) => setIdLabel(Number(e.target.value) || null)} disabled={loading}>
            <option value="">Select a book...</option>
            {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Workflow</label>
          <select className="form-select" value={workflowId} onChange={(e) => handleWorkflowChange(e.target.value)} disabled={loading}>
            {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          {currentWorkflow && <small className="text-muted">{currentWorkflow.description}</small>}
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Prompt</label>
          <textarea className="form-control" rows={3} placeholder="Describe the image you want to generate..."
            value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={loading} />
        </div>

        <div className="row g-3 mb-3">
          <div className="col-auto">
            <label className="form-label">Width</label>
            <input type="number" className="form-control" style={{ width: 100 }} value={width}
              onChange={(e) => setWidth(Number(e.target.value))} min={64} step={16} disabled={loading} />
          </div>
          <div className="col-auto">
            <label className="form-label">Height</label>
            <input type="number" className="form-control" style={{ width: 100 }} value={height}
              onChange={(e) => setHeight(Number(e.target.value))} min={64} step={16} disabled={loading} />
          </div>
          <div className="col-auto">
            <label className="form-label">Batch</label>
            <input type="number" className="form-control" style={{ width: 80 }} value={batch}
              onChange={(e) => setBatch(Number(e.target.value))} min={1} max={10} disabled={loading} />
          </div>
          <div className="col-auto d-flex align-items-end">
            <button type="submit" className="btn btn-primary" disabled={loading || !prompt.trim() || !idLabel}>
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Generating...</>
              ) : "Generate"}
            </button>
          </div>
        </div>

        {currentWorkflow?.supports_lora && (
          <div className="mb-3">
            <label className="form-label fw-semibold">LoRAs</label>
            <div className="d-flex gap-2 mb-2">
              <select className="form-select form-select-sm" defaultValue="" disabled={loading}
                onChange={(e) => { addLora(e.target.value); e.target.value = ""; }}>
                <option value="" disabled>Add a LoRA...</option>
                {availableLoras.filter((name) => !selectedLoras.some((l) => l.lora_name === name))
                  .map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            {selectedLoras.map((lora, i) => (
              <div key={lora.lora_name} className="d-flex align-items-center gap-2 mb-2 p-2 bg-light rounded border">
                <span className="text-truncate" style={{ minWidth: 120, maxWidth: 200 }} title={lora.lora_name}>
                  {lora.lora_name}
                </span>
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted text-nowrap">Model:</small>
                  <input type="range" min="0" max="2" step="0.05" value={lora.strength_model}
                    onChange={(e) => updateLoraStrength(i, "strength_model", parseFloat(e.target.value))}
                    style={{ width: 80 }} disabled={loading} />
                  <small style={{ width: 32 }}>{lora.strength_model.toFixed(2)}</small>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted text-nowrap">Clip:</small>
                  <input type="range" min="0" max="2" step="0.05" value={lora.strength_clip}
                    onChange={(e) => updateLoraStrength(i, "strength_clip", parseFloat(e.target.value))}
                    style={{ width: 80 }} disabled={loading} />
                  <small style={{ width: 32 }}>{lora.strength_clip.toFixed(2)}</small>
                </div>
                <button type="button" className="btn btn-outline-danger btn-sm ms-auto"
                  onClick={() => removeLora(i)} disabled={loading}>&times;</button>
              </div>
            ))}
          </div>
        )}
      </form>

      {loading && !progress && status && (
        <div className="mt-3">
          <small className="text-muted">{status}</small>
          <div className="progress mt-1" style={{ height: 20 }}>
            <div className="progress-bar progress-bar-striped progress-bar-animated bg-secondary" role="progressbar" style={{ width: "100%" }} />
          </div>
        </div>
      )}
      {loading && progress && (
        <div className="mt-3">
          <div className="d-flex justify-content-between mb-1">
            <small className="text-muted">{status || "Generating..."}</small>
            <small className="text-muted">{progress.value}/{progress.max} steps ({pct}%)</small>
          </div>
          <div className="progress" style={{ height: 20 }}>
            <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}

      {selected.size > 0 && (
        <div className="d-flex gap-2 align-items-center mt-3 p-2 bg-light rounded border">
          <strong>{selected.size} selected</strong>
          <button className="btn btn-success btn-sm" disabled={actionLoading} onClick={() => handleAction("accept")}>Accept</button>
          <button className="btn btn-danger btn-sm" disabled={actionLoading} onClick={() => handleAction("decline")}>Decline</button>
        </div>
      )}

      <hr className="my-4" />

      {grouped.length === 0 && !loading && <p className="text-muted text-center">No pending images.</p>}

      {grouped.map((gen) => {
        const groupIds = gen.images.map((img) => img.id);
        const allSelected = groupIds.every((id) => selected.has(id));
        const someSelected = groupIds.some((id) => selected.has(id));

        return (
          <div key={gen.jobId} className="card mb-4">
            <div className="card-header">
              <div className="d-flex align-items-start gap-2">
                <input type="checkbox" className="form-check-input mt-1" checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={() => toggleGroup(gen)} />
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between">
                    <span className="text-truncate me-3" style={{ maxWidth: "75%" }}>
                      <strong>Prompt:</strong> {gen.prompt}
                    </span>
                    <small className="text-muted text-nowrap">{new Date(gen.timestamp).toLocaleString()}</small>
                  </div>
                  <small className="text-muted">Job: {gen.jobId} &middot; Book: {gen.labelName}</small>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {gen.images.map((img) => (
                  <div key={img.id} className="col-auto text-center">
                    <div
                      className={`position-relative d-inline-block rounded border p-1 ${selected.has(img.id) ? "border-primary border-2" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleImage(img.id)}
                    >
                      <input type="checkbox" className="form-check-input position-absolute"
                        style={{ top: 8, left: 8, zIndex: 1 }}
                        checked={selected.has(img.id)}
                        onChange={() => toggleImage(img.id)}
                        onClick={(e) => e.stopPropagation()} />
                      <img src={img.image_url} alt="Generated" className="rounded"
                        style={{ maxHeight: 400, objectFit: "contain" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default GenerateImages;
